import { useAccount, useReadContract, useReadContracts } from 'wagmi'
import { ADDRESSES, STAKING_VAULT_COPY, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import { ERC20_ABI } from '@/lib/erc20'
import stakingVaultAbi from '@/lib/abis/StakingVault.json'
import factoryAbi from '@/lib/abis/StakingVaultFactory.json'
import farmAbi from '@/lib/abis/HorizonEmissions.json'
import { POLL_SLOW, toNumber } from '@/lib/utils'
import { useVaultEmissionRates } from '@/hooks/useVaultEmissionRates'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'


const NOT_DEPLOYED = !IS_LIVE
  || (ADDRESSES.STAKING_VAULT_FACTORY || '').toLowerCase() === ZERO_ADDR
  || !ADDRESSES.STAKING_VAULT_FACTORY

/// The single-asset staking vaults, enumerated from `StakingVaultFactory`, with live totals, the
/// caller's position, and the HORIZON rate each one pays.
///
/// **Enumerated, never hardcoded.** `vaultsView()` returns the whole registry in one call, so
/// listing an asset is a genesis transaction and needs no frontend deploy — the same property
/// `getReservesList()` gives the Markets page. Only the prose lives in the app, in
/// `STAKING_VAULT_COPY`, and an asset with no entry renders without a description rather than
/// disappearing.
///
/// **The emission rate comes from `useVaultEmissionRates`, unchanged.** A `StakingVault` registers
/// in the exact same `HorizonEmissions` farm table a ALM vault does, so the existing hook already
/// returns its rate keyed by address — no second rate source, and no chance of the two disagreeing
/// about what a farm pays. That reuse is the whole reason `StakingVault` implements the share-
/// incentives interface rather than carrying its own reward accounting.
///
/// **No USD APR unless the staked asset has a price.** These assets sit deliberately outside the
/// lending pool — that is the point, since nothing is borrowed against them and so no oracle is
/// required — which usually means there is no feed to value a deposit with. The honest output is
/// HORIZON PER DAY, plus HORIZON per day per staked unit, and `aprBps` is not invented from a price
/// this app does not have. A row showing "—" beats a row showing a confident wrong percentage.
export function useStakingVaults() {
  const { address: user } = useAccount()
  const { horizonPerDayFor } = useVaultEmissionRates()
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()

  const { data: view, refetch: refetchView } = useReadContract({
    address: ADDRESSES.STAKING_VAULT_FACTORY, abi: factoryAbi, functionName: 'vaultsView',
    query: { enabled: !NOT_DEPLOYED, refetchInterval: POLL_SLOW },
  })

  const addresses = view?.[0] ?? []
  const assets = view?.[1] ?? []
  const retiredFlags = view?.[2] ?? []
  const n = addresses.length

  const { data: reads, refetch } = useReadContracts({
    query: { enabled: !NOT_DEPLOYED && n > 0, refetchInterval: POLL_SLOW },
    contracts: addresses.flatMap((vault, i) => [
      { address: vault, abi: stakingVaultAbi, functionName: 'totalSupply' },
      { address: vault, abi: stakingVaultAbi, functionName: 'decimals' },
      { address: vault, abi: stakingVaultAbi, functionName: 'depositsPaused' },
      { address: assets[i], abi: ERC20_ABI, functionName: 'symbol' },
      // The farm's term. Zero is perpetual - the default, and what every farm was before terms
      // existed. `isFarmActive` is read alongside because a farm can be silent for three different
      // reasons (no weight, expired term, severed accountant) and the card says which.
      { address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'farmEndsAt', args: [vault] },
      { address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'isFarmActive', args: [vault] },
    ]),
  })

  const { data: userReads, refetch: refetchUser } = useReadContracts({
    query: { enabled: !NOT_DEPLOYED && n > 0 && !!user, refetchInterval: POLL_SLOW },
    contracts: user
      ? addresses.flatMap((vault, i) => [
          { address: vault, abi: stakingVaultAbi, functionName: 'balanceOf', args: [user] },
          { address: assets[i], abi: ERC20_ABI, functionName: 'balanceOf', args: [user] },
          { address: assets[i], abi: ERC20_ABI, functionName: 'allowance', args: [user, vault] },
          { address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'pendingVaultHorizonOf', args: [vault, user] },
        ])
      : [],
  })

  const vaults = addresses.map((address, i) => {
    // Two different strides: the global batch reads six per vault, the user batch four. Keeping
    // one shared `base` is exactly how a parallel-array read goes silently wrong (CHANGELOG.md
    // item 111), so they are named apart.
    const base = i * 6
    const uBase = i * 4
    const decimals = Number(reads?.[base + 1]?.result ?? 18)
    const totalStakedWei = reads?.[base]?.result ?? 0n
    const totalStaked = toNumber(totalStakedWei, decimals)

    const stakedWei = userReads?.[uBase]?.result ?? 0n
    const walletWei = userReads?.[uBase + 1]?.result ?? 0n
    const pendingHorizon = toNumber(userReads?.[uBase + 3]?.result ?? 0n)

    // Straight from the emissions table, already per-day. `null` means the vault is not registered
    // as a farm at all - distinct from a registered farm sitting at zero weight, which is a real
    // rate of zero, and the two render differently on purpose.
    const horizonPerDay = horizonPerDayFor ? horizonPerDayFor(address) : null

    const copy = STAKING_VAULT_COPY[(assets[i] || '').toLowerCase()] ?? {}

    return {
      address,
      asset: assets[i],
      retired: !!retiredFlags[i],
      symbol: reads?.[base + 3]?.result ?? '—',
      decimals,
      depositsPaused: !!reads?.[base + 2]?.result,
      blurb: copy.blurb ?? null,
      totalStaked,
      totalStakedWei,
      horizonPerDay,
      endsAt: Number(reads?.[base + 4]?.result ?? 0n),
      farmActive: !!reads?.[base + 5]?.result,
      // Per staked unit per day - what is actually knowable without a price for the staked asset,
      // and the figure a depositor can multiply by their own balance.
      horizonPerDayPerUnit: horizonPerDay != null && totalStaked > 0 ? horizonPerDay / totalStaked : null,
      horizonPerDayUsd: horizonPerDay != null && hasHorizonPrice ? horizonPerDay * horizonUsd : null,
      user: {
        staked: toNumber(stakedWei, decimals),
        stakedWei,
        wallet: toNumber(walletWei, decimals),
        walletWei,
        allowance: userReads?.[uBase + 2]?.result ?? 0n,
        pendingHorizon,
        pendingHorizonUsd: hasHorizonPrice ? pendingHorizon * horizonUsd : null,
      },
    }
  })

  function refetchAll() {
    refetchView?.()
    refetch?.()
    refetchUser?.()
  }

  return { vaults, refetch: refetchAll, hasHorizonPrice, notDeployed: NOT_DEPLOYED }
}
